/**
 * A titled block inside a user data panel: heading with an icon, an optional figure or button on
 * the right (stacked below on a phone), an optional sentence of explanation, then the list.
 *
 * Not a Paper — the DataTable underneath already draws one, and a card inside a card is noise.
 */

import { ReactNode } from 'react'
import { Box, Typography } from '@mui/material'

export interface UserDataSectionProps {
  title: string
  icon?: ReactNode
  /** Right-hand content: a total, a count, a button. */
  aside?: ReactNode
  description?: ReactNode
  children: ReactNode
}

export function UserDataSection({ title, icon, aside, description, children }: UserDataSectionProps) {
  return (
    <Box sx={{ mb: 4 }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'space-between',
          gap: 1,
          mb: description ? 0.5 : 1.5,
        }}
      >
        <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600 }}>
          {icon} {title}
        </Typography>
        {aside}
      </Box>
      {description && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {description}
        </Typography>
      )}
      {children}
    </Box>
  )
}
